







































import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import {getFormattedDate, getDurationStr} from '@/utils/dateTimeUtils';
import CursorDiagramToolbar from '@c/toolbar/CursorDiagramToolbar.vue';
import {CursorDiagramModeManager} from '@/logic/cursorDiagramModeManager';
import {CursorLabelModel} from '@/logic/cursors/labelModels/cursorLabelModel';
import {vibrationDiagramModes} from '@/logic/diagramModes';
import {CursorDiagramState} from '@/logic/cursorDiagramState';
import {DiagramModeType, DiagramType, DiagramZoomType} from '@/enums/diagrams';
import {uuidv4} from '@u/uuidUtils';
import diagramStoreModule from '@/store/modules/signalDiagramModule';
import {
  ComparedDiagramData,
  DiagramData,
  DiagramMode,
  DiagramSettings,
} from '@/interfaces/diagrams';
import {DiagramAxisProvider} from '@/logic/diagramAxisProvider';
import {signalColorScheme} from '@/utils/colorSchemes';
import {vibrationDiagnosticsWaterfallDiagramSyncModes} from '@/components/toolbar/configurate';
import SyncMenuButton from '@c/toolbar/SyncMenuButton.vue';
import {DiagramSyncModeType} from '@/components/toolbar/enums';

@Component({
  components: {
    highchart: Chart,
    CursorDiagramToolbar,
    SyncMenuButton,
  },
})
export default class VibrationDiagram extends Vue {
  // Настройки диаграммы
  settings: DiagramSettings = {
    isYLogAxisShow: true,
    isYLogAxis: false,
    hideZeroFrequency: false,
    type: DiagramType.Signal,
    matchColor: signalColorScheme[0].mainColor,
  };

  // Идентификатор диаграммы
  id: string = uuidv4();

  // Режимы диаграммы
  diagramModes: DiagramMode[] = vibrationDiagramModes;

  // Предустановленные режимы
  preselectedModes: DiagramMode[] = [
    this.diagramModes.find(mode => mode.type === DiagramModeType.View) as DiagramMode,
  ];

  // Провайдер для работы с осями
  diagramAxisProvider: DiagramAxisProvider = new DiagramAxisProvider();

  // Менеджер для работы с диаграммой
  modeManager: CursorDiagramModeManager = new CursorDiagramModeManager(
    DiagramType.Signal,
    new CursorLabelModel('', this.xCursorLabelFormatter),
    null,
    this.cursorApplyCallback,
    [],
    (() => {
      const state = new CursorDiagramState();
      state.diagramId = this.id;
      state.activeModes = this.preselectedModes;
      return state;
    })(),
  );

  cursorApplyCallback() {
    if (this.syncApply) {
      diagramStoreModule.diagramSync(this.modeManager.getState());
    }
  }

  @Prop({required: true, default: {}})
  chartData!: ComparedDiagramData;

  @Prop()
  topLevelChart!: any | null;

  @Prop()
  bottomLevelChart!: any | null;

  @Prop({default: 0, type: Number})
  xMinScaleValue!: number;

  // Значение soft min оси x, нужно для того, чтобы при отсутствии данных показывать полный период
  @Prop({type: Number})
  xSoftMin!: number;

  @Prop({type: Number})
  xSoftMax!: number;

  // Показатель того, что применен зум
  @Prop({required: false, type: Boolean, default: false})
  isInZoom!: boolean;

  @Watch('isInZoom')
  onChangeIsInZoom(value: any) {
    if (!value) this.modeManager.clearZoom();
  }

  //Ключ для обновления размеров диаграммы
  @Prop({required: false, type: Number, default: 0})
  resizedKey!: number;

  @Watch('resizedKey')
  onResizedKeyChanged() {
    this.getChart().reflow();
  }

  // Идентификатор основной серии графика
  mainSeriesId = `mainSeries-${this.id}`;

  // Список идентификаторов графиков на диаграмме (нужно знать для подключения курсоров)
  chartSeriesIds: string[] = [this.mainSeriesId];

  newComparedSeriesId(id: string) {
    return `comparedSeries-${id}`;
  }

  // Показатель активности тулбара для работы с графиком
  get toolbarDisabled() {
    return !this.chartData.mainDiagram.data.length;
  }

  get diagramStoreModule() {
    return diagramStoreModule;
  }

  get diagramSyncModes() {
    return vibrationDiagnosticsWaterfallDiagramSyncModes;
  }

  mounted() {
    const chart = this.getChart();
    this.modeManager.init(chart, this.chartSeriesIds, this.xMinScaleValue);
  }

  /*
    Действия при изменении статуса отображения линии курсора
  */
  changeShowCursorLine(show: boolean) {
    //Проводим изменения для текущего графика
    this.modeManager.showLine(show);
    //Если в режиме синхронизации, то отправляем другим графикам
    if (this.syncApply) diagramStoreModule.diagramSync(this.modeManager.getState());
  }

  /*
    Действия при изменении статуса отображения отметок курсора
  */
  changeShowCursorTooltips(show: boolean) {
    this.modeManager.showTooltips(show);
    if (this.syncApply) diagramStoreModule.diagramSync(this.modeManager.getState());
  }

  /*
    Выбор режима графика
  */
  selectModes(modes: DiagramMode[]) {
    this.modeManager.updateState([...modes]);
    diagramStoreModule.diagramSync(this.modeManager.getState());
  }

  /*
    Показатель применения синхронизации к графику
  */
  get syncApply() {
    return (
      this.chartData &&
      diagramStoreModule.syncDiagrams.some(x => x.id === this.chartData.mainDiagram.id)
    );
  }

  @Watch('modeManager.changedZoomXKey')
  onChangeZoomXKey() {
    if (this.syncApply) diagramStoreModule.diagramSync(this.modeManager.getState());
  }

  /*
    Computed поле, привязанное к vuex и watcher для него
  */
  get sync() {
    return diagramStoreModule.sync;
  }
  @Watch('sync')
  syncChanged(sync: {syncModes: any[]; syncDiagrams: any[]; syncedState: CursorDiagramState}) {
    if (!sync.syncDiagrams.some(x => x.id === this.chartData.mainDiagram.id)) return;

    if (
      this.sync.syncModes.some(x => x.type === DiagramSyncModeType.ZoomX) &&
      this.modeManager.zoomType !== DiagramZoomType.X &&
      this.modeManager.zoomType !== DiagramZoomType.None
    ) {
      this.modeManager.zoomType = DiagramZoomType.X;
      this.skipZoom(false);
    }

    const value = sync.syncedState;

    //проверка на то, что это не данная диаграмма
    if (value && value.diagramId === this.id) {
      return;
    }
    const toolbar = this.getToolbar();

    if (value && sync.syncModes.some(x => x.type === DiagramSyncModeType.ZoomX)) {
      const state = new CursorDiagramState(value);
      state.diagramId = this.id;
      this.modeManager.patchZoomState(state, true);

      if (!value.selectedZoomXData?.active) this.skipZoom(false);
      else {
        let interval = value.selectedZoomXData.interval;
        if (interval.dateInterval) interval = interval.dateInterval;
        this.$emit('zoom', interval);
      }
    }

    if (value) {
      //изменяем логику режимов
      const state = new CursorDiagramState(value);
      state.diagramId = this.id;
      this.modeManager.patchCursorState(state, true);
      this.modeManager.showLine(state.isShowLine);
      this.modeManager.showTooltips(state.isShowTooltips);
      this.modeManager.updateDiagram(state.isUpdateDiagram);
    }

    const syncedState = this.modeManager.getState();
    //изменяем представление
    toolbar.setupState(syncedState);
  }

  xFormatter(e: any) {
    return getFormattedDate(e.value, 'HH:mm:ss<br />(dd.MM.yyyy)');
  }

  xCursorLabelFormatter(value: any) {
    return getFormattedDate(value, 'HH:mm:ss (dd.MM)');
  }

  xMarkerBandLabelFormatter(value: any) {
    return getDurationStr(value);
  }

  onItemLegendClicked(e: any) {
    const diagramId = e.target.userOptions.diagramId;
    //у основного графика diagramId нет и мы его не удаляем, только сравниваемые
    if (diagramId)
      this.$emit('itemLegendClick', {diagramId, x: e.browserEvent.x, y: e.browserEvent.y});
  }

  get chartOptions() {
    const series = [];

    if (this.topLevelChart) {
      series.push({
        id: 'topLevel',
        data: this.topLevelChart,
        type: 'line',
        color: 'red',
        dashStyle: 'longdash',
      });
    }
    if (this.bottomLevelChart) {
      series.push({
        id: 'bottomLevel',
        data: this.bottomLevelChart,
        type: 'line',
        color: 'blue',
        dashStyle: 'longdash',
      });
    }

    this.chartSeriesIds = [this.mainSeriesId];

    series.push({
      name: this.chartData.mainDiagram.name,
      id: this.mainSeriesId,
      data: this.chartData.mainDiagram.data,
      type: 'line',
      color: this.settings.matchColor,
      findNearestPointBy: 'xy',
      yAxis: 0,
      zIndex: this.chartData.comparedDiagrams ? this.chartData.comparedDiagrams.length : 0,
    });

    const yAxisData = this.diagramAxisProvider.getYAxis(
      this.chartData,
      signalColorScheme,
      this.settings.isYLogAxis,
    );
    const yAxis = yAxisData.axis;

    this.chartData.comparedDiagrams.forEach((d: DiagramData, index: number) => {
      const comparedSeriesId = this.newComparedSeriesId(uuidv4());
      series.push({
        name: d.name,
        id: comparedSeriesId,
        data: d.data,
        type: 'line',
        color: yAxisData.comparedSeriesColors[index],
        yAxis: yAxisData.comparedSeriesAxisNumbers[index],
        zIndex: index,
        diagramId: d.id,
        visible: d.visible,
      });
      this.chartSeriesIds.push(comparedSeriesId);
    });

    const onItemLegendClicked = this.onItemLegendClicked;
    return {
      credits: {
        enabled: false,
      },
      title: '',
      xAxis: {
        gridLineWidth: 1,
        title: {
          text: 'Время',
        },
        labels: {
          formatter: this.xFormatter,
        },
        softMin: this.xSoftMin,
        softMax: this.xSoftMax,
      },
      yAxis: yAxis,
      //графики
      series,
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        series: {
          states: {
            //отключает выделение точки на графике
            hover: {
              enabled: false,
              halo: {
                size: 0,
              },
              inactive: {
                enabled: false,
              },
            },
          },
          events: {
            legendItemClick: function(e: any) {
              e.preventDefault();
              onItemLegendClicked(e);
            },
          },
        },
        line: {
          turboThreshold: 0,
          boostThreshold: 0,
          marker: {
            enabled: false,
            radius: 0,
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      chart: {
        //animation: false, //общая анимация включена - расскоментировать, чтобы отключить
        events: {
          selection: this.onSelection,
          render: this.onRender,
          afterUpdate: this.afterUpdate,
        },
        zoomType: this.modeManager.zoomType,
      },
      legend: {
        align: 'left',
        verticalAlign: 'bottom',
        borderWidth: 0,
        enabled: true,
      },
      tooltip: {
        enabled: false,
        crosshairs: [false], //отключает линию по умолчанию
      },
    };
  }

  /*
    Действия после обновления графика - необходимо перерисовать
    все, что применено на график с режимов
    т. к. объект графика обновится
  */
  afterUpdate() {
    if (this.sync && diagramStoreModule.syncedState)
      this.modeManager.updateActiveModes(diagramStoreModule.syncedState, this.chartSeriesIds);
    else this.modeManager.updateActiveModes(this.modeManager.getState(), this.chartSeriesIds);
  }

  onSelection(e: any) {
    e.preventDefault();
    const dateInterval = this.modeManager.applyZoom(e.xAxis[0], e.yAxis[0]);
    if (dateInterval) this.$emit('zoom', dateInterval);
  }

  onRender() {
    // Некоторые действия, например отрисовку отметок на диагамме нужно делать на onRender
    // т. к. требуется перерисовка на любое изменение графика (например изменение размера)
    this.modeManager.renderActiveModes();

    const chart = this.getChart();
    if (chart && chart.resetZoomButton) {
      chart.resetZoomButton.hide();
    }
  }

  //Сброс зума
  skipZoom(updateState = true) {
    if (this.modeManager.skipZoom(updateState)) this.$emit('skipZoom');
  }

  //Отмена текущего зума
  undoZoom() {
    const result = this.modeManager.undoZoom();
    if (result === 'skip') this.$emit('skipZoom');
    else if (result) this.$emit('zoom', result);
  }

  getChart() {
    return (this.$refs.chart as any).chart;
  }

  getToolbar() {
    return this.$refs.cursorDiagramToolbar as any;
  }
}
